import { groupBy } from 'lodash-es';

import { concatUniqByProp, isNotNullable, mergeNullable } from 'portlet/common/utils';

import type { View, ViewGroup, ViewGroups, ViewId, ViewWithReference } from './view';
import type { Specification } from './specification';

export const ViewHelper = {
    DEFAULT_VIEW_GROUP_FORM: 'Default',

    merge: (originalView: ViewWithReference, referencedView: View): View => {
        const concatByName = concatUniqByProp('name');

        const datapipe = originalView.datapipe || referencedView.datapipe;
        const signals = mergeNullable(originalView.signals, referencedView.signals, concatByName);
        const repeats = mergeNullable(originalView.repeats, referencedView.repeats, concatByName);

        return Object.assign(
            {},
            referencedView,
            originalView,
            datapipe && { datapipe },
            signals && { signals },
            repeats && { repeats },
        );
    },

    getViewById: (spec: Specification, viewId: ViewId): View | null => {
        const view = spec.views.find(({ id }) => id === viewId);
        if (!view) return null;
        if (!ViewHelper.isReferenceView(view)) return view;

        const referencedView = ViewHelper.getViewById(spec, view.$ref);

        return referencedView ? ViewHelper.merge(view, referencedView) : null;
    },

    isReferenceView: (view: View | ViewWithReference): view is ViewWithReference => '$ref' in view,

    getViewGroups: (spec: Specification): ViewGroups => {
        const views = spec.views
            .map(view => ViewHelper.getViewById(spec, view.id))
            .filter(isNotNullable);

        const viewsGrouped = groupBy(views, view =>
            view.viewGroup ? view.viewGroup.name : Symbol(view.id),
        );

        // Object class methods do not return keys of Symbol type, hence Reflect.ownKeys()
        return Reflect.ownKeys(viewsGrouped).map(key => ({
            name: typeof key === 'symbol' ? null : key,
            members: viewsGrouped[key as string].map(({ id, label, viewGroup }) => ({
                id,
                label,
                form: viewGroup?.form || ViewHelper.DEFAULT_VIEW_GROUP_FORM,
            })),
            defaultView:
                viewsGrouped[key as string].find(view => view.viewGroup?.default)?.id ||
                viewsGrouped[key as string][0].id,
        }));
    },

    getViewGroup: (spec: Specification, { id, label, viewGroup }: View): ViewGroup => {
        return (
            ViewHelper.getViewGroups(spec).find(group => group.name === viewGroup?.name) || {
                name: null,
                members: [{ id, label, form: ViewHelper.DEFAULT_VIEW_GROUP_FORM }],
                defaultView: id,
            }
        );
    },
};
